"use client";

import { useMemo } from "react";
import {
  KEYBOARD_VIEWBOX_HEIGHT,
  keyboardViewBoxWidth,
} from "@/components/ui/molecules/mini-piano";
import type { PhraseNote } from "@/lib/learning-journey/types";
import {
  buildPitchRail,
  PITCH_RAIL_CASING_COLOR,
  PITCH_RAIL_METRICS,
  PITCH_RAIL_OPACITY,
  type PitchRailVariant,
} from "../pitch-rail";

/**
 * A travelling hand's line, drawn **on** the keyboard as a notched rail.
 *
 * See `../pitch-rail.ts` for why the rail replaced the time × pitch
 * polyline, and for the geometry. This file only puts ink on the model.
 *
 * Named `PitchRail` rather than `Rail` because `chord-presentation.tsx`
 * already has a `Rail`: the horizontal time channel under the frieze. They
 * are different objects — that one measures durations, this one lands on
 * keys — and sharing a name would be a trap in review.
 *
 * Every stroke carries `vector-effect="non-scaling-stroke"`. That single
 * attribute is what lets the same drawing hold at 40px in a frieze cell and
 * at 144px in the focus block: widths become screen pixels instead of being
 * scaled with the viewBox. `vector-effect` is not inherited, so it is
 * repeated on each shape rather than hoisted onto a `<g>`.
 */

type PitchRailProps = {
  /** The hand's struck notes, in chronological order. */
  readonly notes: readonly PhraseNote[];
  /** Must match the keyboard underneath. */
  readonly lowMidi: number;
  readonly octaves: number;
  readonly color: string;
  /** Stroke widths — see `pitchRailVariant`. */
  readonly variant: PitchRailVariant;
  /** Baseline in viewBox units — see `pitchRailY`. */
  readonly y: number;
  readonly className?: string;
};

export function PitchRail({
  notes,
  lowMidi,
  octaves,
  color,
  variant,
  y,
  className,
}: PitchRailProps) {
  const rail = useMemo(
    () => buildPitchRail(notes, lowMidi, octaves),
    [notes, lowMidi, octaves],
  );

  if (rail === null) {
    return null;
  }

  const metrics = PITCH_RAIL_METRICS[variant];

  // A zero-length subpath with a round cap renders as a disc whose diameter
  // is the stroke width — which is exactly what `non-scaling-stroke` keeps
  // constant, where an `<circle r=…>` would scale with the viewBox.
  const dot = (x: number): string => `M${x.toFixed(2)},${y} l0,0`;

  return (
    <svg
      viewBox={`0 0 ${keyboardViewBoxWidth(octaves)} ${KEYBOARD_VIEWBOX_HEIGHT}`}
      className={className}
      // Same fit as `MiniPiano`. Its predecessor used `none`: the overlay
      // then stretched to its box while the keyboard letterboxed inside the
      // same box, and the two stopped agreeing on x — up to ~126px apart on
      // a one-octave window in the focus block, where `max-h-[144px]` bites
      // on a 288px-tall intrinsic drawing. No piece in the current corpus
      // hits that case (0 measured occurrences), so this fixes nothing
      // visible today; what it buys is that identical viewBox + identical
      // box + identical fit letterbox identically, which makes alignment
      // unconditional and `keyboardGeometry` no longer load-bearing for it.
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {/* Dark casing first: the rail crosses white keys and black ones, and
          a single stroke legible on one is invisible on the other. */}
      <line
        x1={rail.x1}
        y1={y}
        x2={rail.x2}
        y2={y}
        stroke={PITCH_RAIL_CASING_COLOR}
        strokeWidth={metrics.railCasing}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={rail.x1}
        y1={y}
        x2={rail.x2}
        y2={y}
        stroke={color}
        opacity={PITCH_RAIL_OPACITY}
        strokeWidth={metrics.rail}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* One notch per distinct key, brightening with the order of first
          visit. This is where time went once the time axis was dropped. */}
      {rail.notches.map((notch) => (
        <path
          key={notch.midi}
          d={dot(notch.x)}
          stroke={color}
          opacity={notch.opacity}
          strokeWidth={metrics.notch}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Start: a hollow ring. */}
      <path
        d={dot(rail.startX)}
        stroke={PITCH_RAIL_CASING_COLOR}
        strokeWidth={metrics.endpointCasing}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={dot(rail.startX)}
        stroke={color}
        strokeWidth={metrics.endpoint}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={dot(rail.startX)}
        stroke={PITCH_RAIL_CASING_COLOR}
        strokeWidth={metrics.startHole}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* End: the same disc, filled. Drawn last so that a line returning to
          its starting key still shows an arrival there rather than a ring. */}
      <path
        d={dot(rail.endX)}
        stroke={PITCH_RAIL_CASING_COLOR}
        strokeWidth={metrics.endpointCasing}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={dot(rail.endX)}
        stroke={color}
        strokeWidth={metrics.endpoint}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
